import { PositionCodeEvent, CollectorBatchRequest } from '../core/types';
import { Request } from '../utils/request';
import { generatePositionCode } from '../utils/positionCodeGenerator';

/**
 * 事件上报器
 * 负责批量上报事件，控制上报频率（最小 1 秒间隔）
 */
export class Reporter {
  private baseUrl: string;
  private appCode: string;
  private deviceId: string;
  private userId?: string;
  private debug: boolean;
  private reportInterval: number;
  private maxQueueSize: number;

  private eventQueue: PositionCodeEvent[] = [];
  private currentPositionCode: string = 'unknown';
  private lastReportTime: number = Date.now(); // 初始化为当前时间，避免启动时立即上报
  private reportTimer: number | null = null;
  private debounceTimer: number | null = null; // 防抖定时器
  private isReporting: boolean = false;
  private isPositionCodeReady: boolean = false;

  constructor(
    baseUrl: string,
    appCode: string,
    deviceId: string,
    userId: string | undefined,
    reportInterval: number,
    maxQueueSize: number,
    debug: boolean = false
  ) {
    this.baseUrl = baseUrl;
    this.appCode = appCode;
    this.deviceId = deviceId;
    this.userId = userId;
    this.reportInterval = reportInterval;
    this.maxQueueSize = maxQueueSize;
    this.debug = debug;
  }

  /**
   * 设置当前位置码
   */
  setCurrentPositionCode(pc: string): void {
    const oldPc = this.currentPositionCode;
    this.currentPositionCode = pc;
    this.isPositionCodeReady = true;

    if (this.debug) {
      console.log('[MetricTracker] Reporter position code updated:', oldPc, '->', pc);
    }
  }

  /**
   * 设置用户ID
   */
  setUserId(userId: string | undefined): void {
    this.userId = userId;
    if (this.debug) {
      console.log('[MetricTracker] Reporter user ID updated:', userId);
    }
  }

  /**
   * 添加事件到队列
   */
  addEvent(event: PositionCodeEvent): void {
    // 检查队列大小
    if (this.eventQueue.length >= this.maxQueueSize) {
      if (this.debug) {
        console.warn('[MetricTracker] Event queue is full, dropping oldest event');
      }
      this.eventQueue.shift();
    }

    this.eventQueue.push(event);

    if (this.debug) {
      console.log('[MetricTracker] Event added to queue:', event, 'Queue size:', this.eventQueue.length);
    }

    // 使用防抖机制触发上报
    this.debounceReport();
  }

  /**
   * 启动定时上报
   */
  start(): void {
    if (this.reportTimer) {
      if (this.debug) {
        console.log('[MetricTracker] Reporter already started');
      }
      return;
    }

    // 监听页面卸载事件
    this.setupBeforeUnloadHandler();

    if (this.debug) {
      console.log('[MetricTracker] Reporter started');
    }
  }

  /**
   * 停止定时上报
   */
  stop(): void {
    // 清除防抖定时器
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.debug) {
      console.log('[MetricTracker] Reporter stopped');
    }

    // 最后一次上报
    this.flush();
  }

  /**
   * 防抖上报（在指定时间内没有新事件时才上报）
   */
  private debounceReport(): void {
    // 清除之前的防抖定时器
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
    }

    // 设置新的防抖定时器
    this.debounceTimer = window.setTimeout(() => {
      this.debounceTimer = null;
      this.tryReport();
    }, this.reportInterval);

    if (this.debug) {
      console.log('[MetricTracker] Debounce timer reset, will report in', this.reportInterval, 'ms');
    }
  }

  /**
   * 统一的请求上报方法（批量上报）
   * @param events 要上报的事件数组
   * @param useSendBeacon 是否使用 sendBeacon（用于页面卸载场景）
   */
  private reportEvent(events: PositionCodeEvent[], useSendBeacon: boolean = false): boolean | Promise<boolean> {
    if (events.length === 0) {
      return false;
    }

    const url = `${this.baseUrl}/api/v1/collector`;

    // 批量构造 sourcePc 和 eventTime（以逗号分隔）
    // 如果存在多个事件，仅取第一个和最后一个
    let sourcePcList: string[];
    let eventTimeList: string[];

    if (events.length > 2) {
      // 超过两个时仅取第一个和最后一个
      sourcePcList = [
        generatePositionCode(events[0].sourcePc),
        generatePositionCode(events[events.length - 1].sourcePc)
      ];
      eventTimeList = [
        events[0].eventTime.toISOString(),
        events[events.length - 1].eventTime.toISOString()
      ];
    } else {
      sourcePcList = events.map(e => generatePositionCode(e.sourcePc));
      eventTimeList = events.map(e => e.eventTime.toISOString());
    }

    // 构造请求数据
    const requestData: CollectorBatchRequest = {
      currentPc: generatePositionCode(this.currentPositionCode),
      userId: this.userId,
      deviceId: this.deviceId,
      sourcePc: sourcePcList.join(','),
      eventTime: eventTimeList.join(','),
    };

    if (useSendBeacon && navigator.sendBeacon) {
      // 使用 sendBeacon 发送表单数据（同步方法）
      const formData = new URLSearchParams();
      formData.append('currentPc', requestData.currentPc);
      if (requestData.userId) {
        formData.append('userId', requestData.userId);
      }
      if (requestData.deviceId) {
        formData.append('deviceId', requestData.deviceId);
      }
      formData.append('sourcePc', requestData.sourcePc);
      formData.append('eventTime', requestData.eventTime);

      const success = navigator.sendBeacon(url, formData);

      if (this.debug) {
        console.log('[MetricTracker] sendBeacon result:', success, 'events:', events.length);
      }

      return success;
    } else {
      // 使用普通请求（异步方法）
      return Request.postForm(url, {
        currentPc: requestData.currentPc,
        userId: requestData.userId,
        deviceId: requestData.deviceId,
        sourcePc: requestData.sourcePc,
        eventTime: requestData.eventTime,
      }).then(() => {
        if (this.debug) {
          console.log('[MetricTracker] Events reported successfully, count:', events.length);
        }
        return true;
      }).catch((error) => {
        console.error('[MetricTracker] Failed to report events:', error);
        return false;
      });
    }
  }

  /**
   * 尝试上报（批量上报所有队列中的事件）
   */
  private async tryReport(): Promise<void> {
    // 如果正在上报或队列为空，跳过
    if (this.isReporting || this.eventQueue.length === 0) {
      return;
    }

    // 如果位置码还没准备好，等待
    if (!this.isPositionCodeReady) {
      if (this.debug) {
        console.log('[MetricTracker] Position code not ready, skipping report');
      }
      return;
    }

    this.isReporting = true;
    this.lastReportTime = Date.now();

    try {
      // 取出所有待上报事件进行批量上报
      const eventsToReport = [...this.eventQueue];
      this.eventQueue = [];

      if (this.debug) {
        console.log('[MetricTracker] Reporting events:', eventsToReport.length, 'events');
      }

      // 批量发送请求
      const success = await this.reportEvent(eventsToReport, false);

      if (!success && this.debug) {
        console.warn('[MetricTracker] Events report failed, events may be lost');
      }
    } catch (error) {
      console.error('[MetricTracker] Failed to report events:', error);
      // 注意：失败的事件已经从队列中移除，不会重试
      // 如果需要重试机制，可以在这里实现
    } finally {
      this.isReporting = false;
    }
  }

  /**
   * 立即上报所有事件（批量上报）
   */
  flush(): void {
    if (this.eventQueue.length === 0) {
      return;
    }

    try {
      // 取出所有待上报事件进行批量上报
      // 移除相同页面的相同位置码的跳转的情况
      const eventsToReport = this.eventQueue.filter(event => event.sourcePc !== this.currentPositionCode);

      if (eventsToReport.length === 0) {
        this.eventQueue = [];
        return;
      }

      if (this.debug) {
        console.log('[MetricTracker] Flushing events:', eventsToReport.length, 'events');
      }

      // 优先使用 sendBeacon 确保数据发送
      const success = this.reportEvent(eventsToReport, true) as boolean;
      if (success) {
        this.eventQueue = [];
        return;
      }

      // 备用方案：使用同步 XMLHttpRequest（不推荐，但可以确保发送）
      if (this.debug) {
        console.warn('[MetricTracker] sendBeacon not available or failed, using sync XHR');
      }

      // 构造批量上报数据
      let sourcePcList: string[];
      let eventTimeList: string[];
      if (eventsToReport.length > 2) {
        sourcePcList = [
          generatePositionCode(eventsToReport[0].sourcePc),
          generatePositionCode(eventsToReport[eventsToReport.length - 1].sourcePc)
        ];
        eventTimeList = [
          eventsToReport[0].eventTime.toISOString(),
          eventsToReport[eventsToReport.length - 1].eventTime.toISOString()
        ];
      } else {
        sourcePcList = eventsToReport.map(e => generatePositionCode(e.sourcePc));
        eventTimeList = eventsToReport.map(e => e.eventTime.toISOString());
      }

      const url = `${this.baseUrl}/api/v1/collector`;
      const formData = new URLSearchParams();
      formData.append('currentPc', generatePositionCode(this.currentPositionCode));
      if (this.userId) {
        formData.append('userId', this.userId);
      }
      if (this.deviceId) {
        formData.append('deviceId', this.deviceId);
      }
      formData.append('sourcePc', sourcePcList.join(','));
      formData.append('eventTime', eventTimeList.join(','));

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, false); // 同步请求
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
      xhr.send(formData.toString());

      this.eventQueue = [];
    } catch (error) {
      console.error('[MetricTracker] Failed to flush events:', error);
    }
  }

  /**
   * 设置页面卸载处理
   */
  private setupBeforeUnloadHandler(): void {
    // beforeunload 事件
    window.addEventListener('beforeunload', () => {
      this.flush();
    });

    // visibilitychange 事件（用于处理移动端场景）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });

    // pagehide 事件（更可靠的页面卸载检测）
    window.addEventListener('pagehide', () => {
      this.flush();
    });
  }
}
